type TEntry<TValue> = {
  promise: Promise<TValue | undefined>
  settledAt: number | null
}

/**
 * Fetches one value per key with a shared concurrency cap, and keeps each result so a key is fetched
 * once, not once per caller.
 *
 * - A key that is in flight or fresher than `ttlMs` returns the same promise.
 * - A task that reaches the front of the queue after `isWanted()` turned false, or after `clear()`,
 *   resolves `undefined` without calling the fetcher, and is not cached. `isWanted` should read the
 *   current state (a ref), not a per-call snapshot, because a later caller can share the entry.
 * - A rejection is not cached, so the next request for that key tries again.
 *
 * `TValue` must not include `undefined`: that is how a skipped task is told apart from a result.
 */
export const createKeyedFetchCache = <TValue extends object>({
  fetchValue,
  concurrency,
  ttlMs,
  now = Date.now,
}: {
  fetchValue: (key: string) => Promise<TValue>
  concurrency: number
  ttlMs: number
  now?: () => number
}) => {
  const entries = new Map<string, TEntry<TValue>>()
  const queue: (() => void)[] = []

  let active = 0
  let generation = 0

  const startQueued = () => {
    while (active < concurrency && queue.length > 0) queue.shift()?.()
  }

  const enqueue = <TResult>(task: () => Promise<TResult>) =>
    new Promise<TResult>((resolve, reject) => {
      queue.push(() => {
        active++

        task()
          .then(resolve, reject)
          .finally(() => {
            active--

            startQueued()
          })
      })

      startQueued()
    })

  const reusable = (key: string): Promise<TValue | undefined> | undefined => {
    const existing = entries.get(key)

    if (existing && (existing.settledAt === null || now() - existing.settledAt < ttlMs)) {
      return existing.promise
    }

    return undefined
  }

  const register = (key: string, source: Promise<TValue | undefined>): Promise<TValue | undefined> => {
    const entry: TEntry<TValue> = { promise: Promise.resolve(undefined), settledAt: null }

    const forget = () => {
      if (entries.get(key) === entry) entries.delete(key)
    }

    entry.promise = source.then(
      (value) => {
        if (value === undefined) forget()
        else entry.settledAt = now()

        return value
      },
      (error) => {
        forget()

        throw error
      },
    )

    entries.set(key, entry)

    return entry.promise
  }

  const request = (key: string, isWanted: () => boolean): Promise<TValue | undefined> => {
    const existing = reusable(key)

    if (existing) return existing

    const requestedIn = generation

    return register(
      key,
      enqueue(() =>
        requestedIn === generation && isWanted() ? fetchValue(key) : Promise.resolve(undefined),
      ),
    )
  }

  // A key the fetcher returns no value for resolves `undefined` and is not cached, like a skipped one.
  const requestMany = (
    keys: string[],
    isWanted: (key: string) => boolean,
    fetchMany: (keys: string[]) => Promise<Map<string, TValue>>,
    toBatches: (keys: string[]) => string[][],
  ): Promise<TValue | undefined>[] => {
    const requestedIn = generation
    const promises = new Map(keys.map((key) => [key, reusable(key)]))
    const missing = [...promises].filter(([, promise]) => !promise).map(([key]) => key)

    toBatches(missing).forEach((batch) => {
      const gate: { open: (wanted: Set<string>) => void } = { open: () => undefined }

      const started = new Promise<Set<string>>((resolve) => {
        gate.open = resolve
      })

      const task = enqueue(async () => {
        const wanted = new Set(batch.filter((key) => requestedIn === generation && isWanted(key)))

        gate.open(wanted)

        return wanted.size > 0 ? fetchMany([...wanted]) : new Map<string, TValue>()
      })

      batch.forEach((key) =>
        promises.set(
          key,
          register(
            key,
            started.then((wanted) => (wanted.has(key) ? task.then((values) => values.get(key)) : undefined)),
          ),
        ),
      )
    })

    return keys.map((key) => promises.get(key) ?? Promise.resolve(undefined))
  }

  // Drops every result and every task that has not started yet. Requests already in flight finish and
  // resolve for their caller, but are not kept.
  const clear = () => {
    generation++

    entries.clear()
  }

  return { request, requestMany, clear }
}
